import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import {
  TENANT_PRODUCT_FLAG_KEYS,
  TENANT_PRODUCT_FLAG_META,
  TENANT_PRODUCT_FLAG_PLAN_TIER,
  type PlanTemplate,
  type TenantProductFlagKey,
  mergeTenantProductFlags,
} from "@/lib/tenant-product-flags";

type TenantOption = { id: string; name: string; slug: string };

// ── Overrides por tenant ──────────────────────────────────────────────────────
// SCRUM-300: el catálogo global de features (antes un tab acá, duplicado de
// FeatureCatalogPanel — mismo endpoint /api/admin/feature-flags, misma data,
// sin agrupar) se sacó de este componente. FeatureCatalogPanel (tab
// "feature-catalog") es ahora la única pantalla para el catálogo global.
// Este componente queda enfocado en lo que no tiene equivalente en ningún
// otro lado: flags/overrides por workspace puntual.

export function AdminFlagsPanel({ tenants }: { tenants: TenantOption[] }) {
  const [tenantId, setTenantId] = useState("");
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [flags, setFlags] = useState<Record<TenantProductFlagKey, boolean> | null>(null);
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!tenantId && tenants[0]) setTenantId(tenants[0].id);
  }, [tenantId, tenants]);

  const selectedWorkspace = useMemo(
    () => tenants.find((t) => t.id === tenantId) ?? null,
    [tenants, tenantId],
  );

  const loadAll = useCallback(async (tid: string) => {
    if (!tid) return;
    setLoading(true);
    try {
      const [flagsRes, ovRes] = await Promise.all([
        apiGetJson<{ flags: Record<TenantProductFlagKey, boolean> }>(
          `/api/admin/tenant-flags?tenantId=${encodeURIComponent(tid)}`,
        ),
        apiGetJson<{ overrides: Record<string, boolean> }>(
          `/api/admin/tenant-overrides?tenantId=${encodeURIComponent(tid)}`,
        ),
      ]);
      setFlags(flagsRes.flags);
      setOverrides(ovRes.overrides);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al cargar");
      setFlags(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tenantId) void loadAll(tenantId);
  }, [tenantId, loadAll]);

  const patchFlag = async (key: TenantProductFlagKey, value: boolean) => {
    if (!tenantId) return;
    setSaving(key);
    try {
      const r = await apiSendJson<{ flags: Record<TenantProductFlagKey, boolean> }>(
        "/api/admin/tenant-flags",
        "PATCH",
        { tenantId, key, value },
      );
      setFlags(r.flags);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
      void loadAll(tenantId);
    } finally {
      setSaving(null);
    }
  };

  const patchOverride = async (key: TenantProductFlagKey, value: boolean) => {
    if (!tenantId) return;
    setSaving(`ov_${key}`);
    try {
      const r = await apiSendJson<{ overrides: Record<string, boolean> }>(
        "/api/admin/tenant-overrides",
        "PATCH",
        { tenantId, key, value },
      );
      setOverrides(r.overrides);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally {
      setSaving(null);
    }
  };

  const applyTemplate = async (template: PlanTemplate) => {
    if (!tenantId) return;
    setApplying(true);
    try {
      const r = await apiSendJson<{ flags: Record<TenantProductFlagKey, boolean> }>(
        "/api/admin/tenant-flags",
        "PATCH",
        { tenantId, template },
      );
      setFlags(r.flags);
      toast.success(`Plantilla ${template} aplicada`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally {
      setApplying(false);
    }
  };

  const templateColors: Record<PlanTemplate, string> = {
    starter: "border-slate-500/40 text-slate-300 hover:bg-slate-500/10",
    pro: "border-violet-500/40 text-violet-300 hover:bg-violet-500/10",
    business: "border-amber-500/40 text-amber-300 hover:bg-amber-500/10",
    enterprise: "border-rose-500/40 text-rose-300 hover:bg-rose-500/10",
  };

  const tierBadge: Record<"starter" | "pro" | "business" | "enterprise", string> = {
    starter: "bg-slate-500/20 text-slate-400",
    pro: "bg-violet-500/20 text-violet-300",
    business: "bg-amber-500/20 text-amber-300",
    enterprise: "bg-rose-500/20 text-rose-300",
  };

  return (
    <div className="space-y-4">
      <p className="max-w-2xl text-xs text-muted-foreground">
        Dos niveles: <strong>Flags del plan</strong> (plantilla aplicada al workspace) y <strong>Extras</strong>
        (features concedidas puntualmente más allá del plan, sin cambiar la plantilla).
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <p className="mb-1 text-xs text-muted-foreground">Workspace</p>
          <Popover open={workspaceOpen} onOpenChange={setWorkspaceOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                role="combobox"
                aria-expanded={workspaceOpen}
                data-testid="admin-flags-workspace-search"
                className="h-9 w-[280px] justify-between bg-secondary/50 px-3 text-xs font-normal"
              >
                <span className="truncate">
                  {selectedWorkspace
                    ? `${selectedWorkspace.name} · ${selectedWorkspace.slug}`
                    : "Buscar workspace…"}
                </span>
                <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Nombre o slug…" className="h-9" />
                <CommandList>
                  <CommandEmpty>Sin resultados</CommandEmpty>
                  <CommandGroup>
                    {tenants.map((t) => (
                      <CommandItem
                        key={t.id}
                        value={`${t.name} ${t.slug} ${t.id}`}
                        onSelect={() => {
                          setTenantId(t.id);
                          setWorkspaceOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-3.5 w-3.5",
                            tenantId === t.id ? "opacity-100" : "opacity-0",
                          )}
                        />
                        <span className="min-w-0 flex-1 truncate">{t.name}</span>
                        <span className="ml-2 shrink-0 font-mono text-[10px] text-muted-foreground">
                          {t.slug}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        {tenantId && (
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-xs text-muted-foreground">Plantilla:</p>
            {(["starter", "pro", "business", "enterprise"] as PlanTemplate[]).map((plan) => (
              <Button
                key={plan}
                size="sm"
                variant="outline"
                className={`h-8 text-xs capitalize ${templateColors[plan]}`}
                disabled={!tenantId || applying}
                onClick={() => void applyTemplate(plan)}
              >
                {applying ? <Loader2 className="h-3 w-3 animate-spin" /> : plan}
              </Button>
            ))}
          </div>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
        </div>
      )}

      {flags && !loading && (
        <>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Flags del plan</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {TENANT_PRODUCT_FLAG_KEYS.map((key) => {
              const tier = TENANT_PRODUCT_FLAG_PLAN_TIER[key];
              const hasExtra = overrides[key] === true;
              return (
                <div key={key} className="glass-card flex flex-col gap-2 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-foreground">{TENANT_PRODUCT_FLAG_META[key]}</p>
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium capitalize ${tierBadge[tier]}`}>
                          {tier}
                        </span>
                        {hasExtra && (
                          <span className="rounded bg-emerald-500/20 px-1.5 py-0.5 text-[10px] font-medium text-emerald-300">
                            extra
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 font-mono text-[10px] text-muted-foreground">{key}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] text-muted-foreground">Plan</span>
                        <Switch
                          checked={flags[key]}
                          disabled={saving === key || applying}
                          onCheckedChange={(v) => void patchFlag(key, v)}
                        />
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] text-muted-foreground">Extra</span>
                        <Switch
                          checked={hasExtra}
                          disabled={saving === `ov_${key}` || applying}
                          onCheckedChange={(v) => void patchOverride(key, v)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export { mergeTenantProductFlags };
